import React, { useState, useEffect, useContext, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  Box,
  Grid,
  Divider,
} from "@mui/material";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import SignInLayout from "layouts/authentication/components/SignInLayout";
import WelcomeModal from "layouts/authentication/components/WelcomeModal";
import axiosInstance from "api/axiosInstance";
import { useAuth } from "../../../AuthContext";
import { LanguageContext } from "../../../context/LanguageContext";
import { getAuthRedirectPath } from "../../../utils/referral";
import { COLORS, TYPO } from "theme/tokens";

import "./index.css";

function TwoFAForm() {
  const { componentLanguageData, loadLanguageFile, language } = useContext(LanguageContext);
  useEffect(() => {
    loadLanguageFile("two_fa", "authentication/two-fa/two-fa_lang");
  }, [language, loadLanguageFile]);
  const LANGUAGE = useMemo(
    () => componentLanguageData.two_fa || { Main: {}, Alert: {}, Fistlogin: {} },
    [componentLanguageData]
  );
  const [token, setToken] = useState("");
  const [showLowTokenDialog, setShowLowTokenDialog] = useState(false);
  const [showNoGAWarning, setShowNoGAWarning] = useState(false);
  const [openWelcome, setOpenWelcome] = useState(false);
  const [tokenLen, setTokenLen] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const email = localStorage.getItem("pendingVerifyEmail");
      const loginSource = localStorage.getItem("loginSource");
      if (!email) {
        toast.error(LANGUAGE.Alert.Content1);
        navigate("/authentication/sign-in");
        return;
      }

      // 修補 C3b/H6：帶上密碼階段的 pending token
      const pendingToken = localStorage.getItem("pendingToken");
      const res = await axiosInstance.post("/auth/verify-token", { email, token, loginSource, pendingToken });

      try {
        localStorage.setItem("access_token", res.data.accessToken);
        localStorage.setItem("user", JSON.stringify(res.data.user));
        localStorage.removeItem("loginSource");
        localStorage.removeItem("pendingToken");
      } catch (err) {
        console.warn("⚠️ localStorage 寫入失敗：", err);
      }
      const auth = res.data.auth;
      // 修補 H17：setState 非同步，下面不可讀 state 變數 tokenLen（會是上一輪舊值）→直接用回應值判斷
      const newTokenLen = res.data.token_len;
      setTokenLen(newTokenLen);
      const firstLogin = res.data.firstLogin;
      localStorage.setItem("firstLogin", firstLogin);
      if (firstLogin === true) setOpenWelcome(true);
      if (auth === "false") {
        setShowNoGAWarning(true);
      } else if (newTokenLen === "false") {
        setShowLowTokenDialog(true);
      } else {
        const user = JSON.parse(localStorage.getItem("user"));
        const accessToken = localStorage.getItem("access_token");
        login(accessToken, user);
        navigate(getAuthRedirectPath());
      }
    } catch (err) {
      if (err.response?.data?.message === "Error_pending_invalid") {
        // 修補 C3b/H6：pending token 過期/無效 → 導回登入頁重新登入
        toast.error(LANGUAGE.Alert.Content10 || "登入逾時，請重新登入");
        localStorage.removeItem("pendingToken");
        navigate("/authentication/sign-in");
        return;
      } else if (err.response?.data?.message === "Error_verifyR_002") {
        toast.error(LANGUAGE.Alert.Content2);
      } else if (err.response?.data?.message === "Error_verifyR_001") {
        toast.error(LANGUAGE.Alert.Content3);
      } else {
        toast.error(LANGUAGE.Alert.Content4);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async (e) => {
    if (e) e.preventDefault();
    try {
      const email = localStorage.getItem("pendingVerifyEmail");
      if (!email) {
        toast.error(LANGUAGE.Alert.Content1);
        return;
      }
      await axiosInstance.post("/auth/regenerate_email", { email });
      toast.success(LANGUAGE.Alert.Content8);
    } catch (error) {
      toast.error(LANGUAGE.Alert.Content9);
    }
  };

  // 與忘記密碼頁一致：bg #F6F6F6 / radius 6px / 內文 bodyM #000 / placeholder bodyM #C4C4C4
  const filledInputSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: COLORS.grayLightest,
      borderRadius: "6px",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused": {
        backgroundColor: COLORS.white,
        "& fieldset": { border: "1px solid", borderColor: COLORS.main },
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "12px 8px 8px 8px",
      ...TYPO.bodyM,
      color: COLORS.black,
      "&::placeholder": { ...TYPO.bodyM, color: COLORS.grayMedian, opacity: 1 },
    },
  };

  const cardLogo =
    language === "zh-TW" || language === "zh-CN"
      ? "/images/logo/logo_2_1.png"
      : "/images/logo/logo_2_2.png";

  return (
    <SignInLayout>
      <Card
        sx={{
          width: "100%",
          maxWidth: 920,
          minHeight: { xs: "auto", md: 600 },
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
        }}
      >
        <Grid container sx={{ minHeight: "inherit" }}>
          {/* === 左欄：品牌（白底 + 右框線 + logo 162×254 置中）=== */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: { xs: "none", md: "flex" },
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: COLORS.white,
              borderRight: `1px solid ${COLORS.grayLighter}`,
              px: 10,
              py: 8,
            }}
          >
            <MDBox component="img" src={cardLogo} alt="MASQuant" sx={{ width: 162, height: 254, objectFit: "contain" }} />
          </Grid>

          {/* === 右欄：表單 === */}
          <Grid
            item
            xs={12}
            md={6}
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              background: COLORS.white,
              p: { xs: 3, sm: 5, md: 8 },
            }}
          >
            <MDBox sx={{ width: "100%", maxWidth: 320 }}>
              {/* 手機版 logo */}
              <MDBox display={{ xs: "flex", md: "none" }} justifyContent="center" mb={3}>
                <MDBox component="img" src={cardLogo} alt="MASQuant" sx={{ maxHeight: 120, objectFit: "contain" }} />
              </MDBox>

              {/* 雙重驗證 — displayM / #000 / 500 */}
              <MDTypography sx={{ ...TYPO.displayM, color: COLORS.black, fontWeight: 500, mb: 1 }}>
                {LANGUAGE.Main.Content1}
              </MDTypography>
              {/* 副標 — bodyM / grayDark / 400 */}
              <MDTypography sx={{ ...TYPO.bodyM, color: COLORS.grayDark, fontWeight: 400, mb: 3 }}>
                {LANGUAGE.Main.Content2}
                <br />
                {LANGUAGE.Main.Content3}
              </MDTypography>

              <MDBox component="form" onSubmit={handleVerify}>
                {/* OTP / Account Token* — bodyS / #000 / 400 */}
                <MDBox mb={2}>
                  <MDTypography sx={{ ...TYPO.bodyS, color: COLORS.black, fontWeight: 400, display: "block", mb: 1 }}>
                    {LANGUAGE.Main.Content16}
                    <span style={{ ...TYPO.bodyS, color: COLORS.main }}>*</span>
                  </MDTypography>
                  <MDInput
                    type="text"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    fullWidth
                    required
                    sx={filledInputSx}
                  />
                </MDBox>

                {/* 驗證 — 高 60 / radius 16 / 漸層 / title / #FFF / 500 */}
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  fullWidth
                  disabled={loading || !token}
                  sx={{
                    height: 60,
                    borderRadius: "16px",
                    background: COLORS.gradient,
                    boxShadow: "4px 4px 9px 0 rgba(222, 86, 15, 0.20)",
                    textTransform: "none",
                    ...TYPO.title,
                    color: `${COLORS.white} !important`,
                    fontWeight: 500,
                    "&:hover": {
                      background: COLORS.gradient,
                      boxShadow: "4px 4px 12px 0 rgba(222, 86, 15, 0.30)",
                    },
                    "&:disabled": {
                      background: COLORS.gradient,
                      color: `${COLORS.white} !important`,
                      opacity: 0.6,
                    },
                  }}
                >
                  {loading ? LANGUAGE.Alert.Content5 : LANGUAGE.Alert.Content6}
                </MDButton>

                {/* 分隔線 */}
                <Divider sx={{ my: 3, borderColor: COLORS.grayLighter }} />

                {/* 重新寄送 Account Tokens（refresh icon + title/#F36D26/500）*/}
                <MDBox display="flex" justifyContent="center" alignItems="center">
                  <MDBox
                    component="a"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      handleResend();
                    }}
                    className="auth-underline"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.75,
                      cursor: "pointer",
                      textDecoration: "none",
                    }}
                  >
                    <Icon sx={{ fontSize: "16px !important", color: COLORS.main }}>refresh</Icon>
                    <span style={{ ...TYPO.title, color: COLORS.main, fontWeight: 500 }}>{LANGUAGE.Main.Content17}</span>
                  </MDBox>
                </MDBox>
              </MDBox>
            </MDBox>
          </Grid>
        </Grid>
      </Card>

      {loading && (
        <div className="loading-overlay">
          <CircularProgress color="inherit" />
        </div>
      )}

      <Dialog
        open={showNoGAWarning}
        onClose={() => setShowNoGAWarning(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}
      >
        <DialogTitle sx={{ pt: 2.5, px: 3, pb: 1 }}>
          <MDTypography sx={{ ...TYPO.headingM, color: COLORS.main }}>
            {LANGUAGE.Main.Content4}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2.5 }}>
          <MDTypography sx={{ ...TYPO.bodyM, color: COLORS.grayDark, lineHeight: 1.8 }}>
            {LANGUAGE.Main.Content5}{" "}
            <strong style={{ color: COLORS.main }}>Authenticator</strong>{" "}
            {LANGUAGE.Main.Content6}
            <br />
            {LANGUAGE.Main.Content7}
          </MDTypography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, gap: 1.5 }}>
          {/* 稍後再說 — 次要（灰框） */}
          <Button
            disableRipple
            onClick={() => {
              setShowNoGAWarning(false);
              if (tokenLen === "false") {
                setShowLowTokenDialog(true);
              } else {
                const user = JSON.parse(localStorage.getItem("user"));
                const accessToken = localStorage.getItem("access_token");
                if (accessToken && user) {
                  login(accessToken, user);
                  navigate(getAuthRedirectPath());
                } else {
                  toast.error(LANGUAGE.Alert.Content7);
                  navigate("/authentication/sign-in");
                }
              }
            }}
            sx={{
              ...TYPO.bodyM,
              textTransform: "none",
              color: COLORS.grayDark,
              border: `1px solid ${COLORS.grayLighter}`,
              borderRadius: "8px",
              px: 2.5,
              py: 1,
              boxShadow: "none",
              "&:hover": {
                background: "transparent",
                border: `1px solid ${COLORS.grayMedian}`,
                boxShadow: "none",
              },
            }}
          >
            {LANGUAGE.Main.Content8}
          </Button>

          {/* 前往啟用 — 主要（實心橘 #F36D26） */}
          <Button
            disableRipple
            onClick={() => {
              setShowNoGAWarning(false);
              const user = JSON.parse(localStorage.getItem("user"));
              const accessToken = localStorage.getItem("access_token");
              if (accessToken && user) {
                login(accessToken, user);
                navigate("/2fa/manage-ga");
              } else {
                toast.error(LANGUAGE.Alert.Content7);
                navigate("/authentication/sign-in");
              }
            }}
            sx={{
              ...TYPO.bodyM,
              textTransform: "none",
              color: `${COLORS.white} !important`,
              background: COLORS.main,
              borderRadius: "8px",
              px: 2.5,
              py: 1,
              boxShadow: "none",
              "&:hover": { background: COLORS.main, boxShadow: "none" },
            }}
          >
            {LANGUAGE.Main.Content9}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showLowTokenDialog}
        onClose={() => setShowLowTokenDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <MDTypography variant="h6" fontWeight="bold" sx={{ color: COLORS.error }}>
            {LANGUAGE.Main.Content10}
          </MDTypography>
        </DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text">
            {LANGUAGE.Main.Content11} <strong>Account Tokens</strong>{" "}
            {LANGUAGE.Main.Content12}
            <br />
            {LANGUAGE.Main.Content13}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setShowLowTokenDialog(false);
              const user = JSON.parse(localStorage.getItem("user"));
              const accessToken = localStorage.getItem("access_token");
              if (accessToken && user) {
                login(accessToken, user);
                navigate(getAuthRedirectPath());
              } else {
                toast.error(LANGUAGE.Alert.Content7);
                navigate("/authentication/sign-in");
              }
            }}
          >
            {LANGUAGE.Main.Content14}
          </Button>

          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              setShowLowTokenDialog(false);
              const user = JSON.parse(localStorage.getItem("user"));
              const accessToken = localStorage.getItem("access_token");
              if (accessToken && user) {
                login(accessToken, user);
                navigate("/2fa/tokens");
              } else {
                toast.error(LANGUAGE.Alert.Content7);
                navigate("/authentication/sign-in");
              }
            }}
          >
            {LANGUAGE.Main.Content15}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 2026-06-08 首登歡迎 modal 改用共用元件 WelcomeModal（截圖二版型）*/}
      <WelcomeModal
        open={openWelcome}
        onClose={() => setOpenWelcome(false)}
        onExperience={() => {
          setOpenWelcome(false);
          const user = JSON.parse(localStorage.getItem("user"));
          const accessToken = localStorage.getItem("access_token");
          if (accessToken && user) {
            login(accessToken, user);
            navigate(getAuthRedirectPath());
          } else {
            toast.error(LANGUAGE.Alert.Content7);
            navigate("/authentication/sign-in");
          }
        }}
        onLearnMore={() => {
          setOpenWelcome(false);
          const user = JSON.parse(localStorage.getItem("user"));
          const accessToken = localStorage.getItem("access_token");
          if (accessToken && user) {
            login(accessToken, user);
            navigate("/plans");
          } else {
            toast.error(LANGUAGE.Alert.Content7);
            navigate("/authentication/sign-in");
          }
        }}
      />
    </SignInLayout>
  );
}

export default TwoFAForm;
